/**
 * Where an agent run's skills come from (YAG-784): the repository's own
 * committed skills (on unless an owner switched them off, YAG-1006), the
 * workspace's plugin marketplace, and a person's own skills repository. YAGNI
 * stores pointers, never copies: each source is fetched at run start and
 * pinned to a commit.
 *
 * The backend writes `sources.json` (version 1) into the sandbox's mission
 * directory and the CLI reads it; the Receipt lists one `AgentSourceRecord`
 * per source. The three settings routes speak the DTOs below.
 *
 * This module runs in the browser too, so it uses nothing from Node.
 */
/** Where the backend extracts the org and personal sources in the sandbox. */
export const AGENT_SOURCES_ROOT = "/opt/yagni/sources";
export const AGENT_SOURCES_ORG_DIR = `${AGENT_SOURCES_ROOT}/org`;
export const AGENT_SOURCES_PERSONAL_DIR = `${AGENT_SOURCES_ROOT}/personal`;
/** The mission file name, beside ticket.md in `$YAGNI_MISSION_DIR`. */
export const AGENT_RUN_SOURCES_FILE_NAME = "sources.json";
/** The `workspace_settings` key the org plugin marketplace pointer lives under. */
export const PLUGIN_MARKETPLACE_SETTING_KEY = "agent_runs.plugin_marketplace";
/** Bounds on stored values. A bound on what is kept, not a suggestion. */
export const MAX_ENABLED_PLUGINS = 50;
export const MAX_PLUGIN_NAME_LENGTH = 100;
export const MAX_GIT_REF_LENGTH = 255;
/**
 * GitHub's own rule: alphanumerics and single inner hyphens, at most 39, plus
 * `_` for Enterprise Managed User owners (`jane_acme`). Matches the CLI.
 */
const GITHUB_OWNER = /^[A-Za-z0-9](?:[A-Za-z0-9_]|-(?=[A-Za-z0-9_])){0,38}$/;
/** Letters, digits, `.`, `_`, `-`; at most 100. `.` and `..` refused below. */
const GITHUB_REPO_NAME = /^[A-Za-z0-9._-]{1,100}$/;
/**
 * A repository named as a person would paste it, normalized to `owner/name`:
 *
 *   owner/name
 *   https://github.com/owner/name   (optional `.git`, trailing `/`, `www.`)
 *   git@github.com:owner/name.git
 *
 * GitHub only. Anything else, including a path past the repository
 * (`/tree/main`), a query, or a character GitHub does not allow, is null.
 */
export function parseGitHubRepoInput(input) {
    if (typeof input !== "string")
        return null;
    const raw = input.trim();
    if (!raw || raw.length > 300)
        return null;
    let path;
    const ssh = /^git@github\.com:(.+)$/i.exec(raw);
    const web = /^(?:https?:\/\/)?(?:www\.)?github\.com\/(.+)$/i.exec(raw);
    if (ssh)
        path = ssh[1];
    else if (web)
        path = web[1].replace(/\/$/, "");
    else if (!raw.includes(":"))
        path = raw;
    else
        return null;
    path = path.replace(/\.git$/i, "");
    const parts = path.split("/");
    if (parts.length !== 2)
        return null;
    const [owner, name] = parts;
    if (!GITHUB_OWNER.test(owner))
        return null;
    if (!GITHUB_REPO_NAME.test(name) || name === "." || name === "..")
        return null;
    return { owner, name, fullName: `${owner}/${name}` };
}
/**
 * A branch, tag or commit as a person would type it, or null for "the
 * default branch". Refuses what git refuses that matters here (`..`, a
 * leading `-` or `/`, a trailing `/` or `.lock`, `@{`) and anything outside a
 * strict charset. A ref may hold `/`: encode it before it goes in a URL.
 */
export function parseGitRefInput(input) {
    if (input === undefined || input === null)
        return { ok: true, value: null };
    if (typeof input !== "string")
        return { ok: false, error: "The branch must be text." };
    const ref = input.trim();
    if (!ref)
        return { ok: true, value: null };
    if (ref.length > MAX_GIT_REF_LENGTH ||
        !/^[A-Za-z0-9._/-]+$/.test(ref) ||
        ref.includes("..") ||
        ref.includes("//") ||
        ref.startsWith("-") ||
        ref.startsWith("/") ||
        ref.endsWith("/") ||
        ref.endsWith(".") ||
        ref.endsWith(".lock")) {
        return { ok: false, error: "That isn't a branch, tag or commit name GitHub accepts." };
    }
    return { ok: true, value: ref };
}
/**
 * The plugins a workspace enables from its marketplace: trimmed, de-duplicated,
 * order kept. Refused with a sentence the route can return as-is.
 */
export function parseEnabledPlugins(value) {
    if (!Array.isArray(value))
        return { ok: false, error: "enabledPlugins must be a list of plugin names." };
    if (value.length > MAX_ENABLED_PLUGINS) {
        return { ok: false, error: `At most ${MAX_ENABLED_PLUGINS} plugins.` };
    }
    const out = [];
    for (const entry of value) {
        const name = typeof entry === "string" ? entry.trim() : "";
        if (!name ||
            name.length > MAX_PLUGIN_NAME_LENGTH ||
            !/^[A-Za-z0-9._-]+$/.test(name) ||
            /^\.+$/.test(name)) {
            return { ok: false, error: "Every plugin name must be letters, digits, '.', '_' or '-'." };
        }
        if (!out.includes(name))
            out.push(name);
    }
    return { ok: true, value: out };
}
//# sourceMappingURL=agentSources.js.map